import type {
	Control,
	FieldErrors,
	FieldPath,
	FieldValues,
} from "react-hook-form";

import { Controller } from "react-hook-form";

import { Input } from "@/components/ui/input/input";

import {
	getLatitudeError,
	getLongitudeError,
} from "../../../utils/shared/coordinates";

type CoordinateFieldsProps<T extends CoordinateFieldsValues & FieldValues> = {
	control: Control<T>;
	errors: FieldErrors<T>;
};

type CoordinateFieldsValues = {
	latitude: string;
	longitude: string;
};

/**
 * Shared lat/lng Controllers for map placement forms (sensor create; location
 * keeps its own fields bundled with address autofill).
 */
export const CoordinateFields = <
	T extends CoordinateFieldsValues & FieldValues,
>({
	control,
	errors,
}: CoordinateFieldsProps<T>) => (
	<div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
		<Controller
			control={control}
			name={"latitude" as FieldPath<T>}
			render={({ field }) => {
				const latitudeError =
					(errors.latitude?.message as string | undefined) ??
					getLatitudeError(String(field.value ?? ""));
				return (
					<Input
						className="w-full"
						errorMessage={latitudeError}
						inputMode="decimal"
						isInvalid={Boolean(latitudeError)}
						label="Latitude"
						onBlur={field.onBlur}
						onChange={field.onChange}
						placeholder="E.g. 39.7392"
						ref={field.ref}
						type="number"
						value={String(field.value ?? "")}
					/>
				);
			}}
			rules={{
				validate: (value) => getLatitudeError(String(value ?? "")) ?? true,
			}}
		/>
		<Controller
			control={control}
			name={"longitude" as FieldPath<T>}
			render={({ field }) => {
				const longitudeError =
					(errors.longitude?.message as string | undefined) ??
					getLongitudeError(String(field.value ?? ""));
				return (
					<Input
						className="w-full"
						errorMessage={longitudeError}
						inputMode="decimal"
						isInvalid={Boolean(longitudeError)}
						label="Longitude"
						onBlur={field.onBlur}
						onChange={field.onChange}
						placeholder="E.g. -104.9903"
						ref={field.ref}
						type="number"
						value={String(field.value ?? "")}
					/>
				);
			}}
			rules={{
				validate: (value) => getLongitudeError(String(value ?? "")) ?? true,
			}}
		/>
	</div>
);
